import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { Declaration, Folder, States } from "@app/models/folder";
import { environment } from "src/environments/environment";
import { PaginateList } from "@app/models/paginateList";
import { PaginateFilter } from "@app/models/filters/paginateFilter";
import { FolderListFilter } from "@app/models/filters/folderListFilter";
import { Configuration } from "@app/models/configuration";
import { FileListFilter } from "@app/models/filters/fileListFilter";
import { File } from "@app/models/file";

const baseUrl = environment.servicesDataUrl;

@Injectable({
    providedIn: "root",
})
export class FolderService {
    private folderListSubject = new BehaviorSubject<PaginateList<Folder>>(new PaginateList());
    folderList = this.folderListSubject.asObservable();
    private folderListFilterSubject = new BehaviorSubject<FolderListFilter>(new FolderListFilter());
    folderListFilter = this.folderListFilterSubject.asObservable();
    private attachmentListSubject = new BehaviorSubject<PaginateList<File>>(new PaginateList());
    attachmentList = this.attachmentListSubject.asObservable();

    constructor(private http: HttpClient) {}

    setFolderList(folderList: PaginateList<Folder>): void {
        this.folderListSubject.next(folderList);
    }

    getFolderList(): PaginateList<Folder> {
        return this.folderListSubject.getValue();
    }

    setFolderListFilters(folderFilterList: FolderListFilter): void {
        this.folderListFilterSubject.next(folderFilterList);
    }

    getFolderListFilters(): FolderListFilter {
        return this.folderListFilterSubject.getValue();
    }

    setAttachmentList(attachmentList: PaginateList<File>): void {
        this.attachmentListSubject.next(attachmentList);
    }

    getAttachmentList(): PaginateList<File> {
        return this.attachmentListSubject.getValue();
    }

    getAll(filters: PaginateFilter<FolderListFilter>): Observable<PaginateList<Folder>> {
        return this.http.post<PaginateList<Folder>>(`${baseUrl}/fascicoli`, filters);
    }

    export(filters: PaginateFilter<FolderListFilter>, type: string): Observable<HttpResponse<Blob>> {
        const params = new HttpParams().set("tipo", type);
        return this.http.post(`${baseUrl}/fascicoli/esporta`, filters, {
            params,
            responseType: "blob",
            observe: "response",
        });
    }

    get(id: string): Observable<Folder> {
        return this.http.get<Folder>(`${baseUrl}/fascicolo/${id}`);
    }

    getAttachment(id: number): Observable<Blob> {
        return this.http.get(`${baseUrl}/fascicolo/allegato/${id}`, { responseType: "blob" });
    }

    getAttachments(id: string | number, filters?: PaginateFilter<FileListFilter>): Observable<PaginateList<File>> {
        return this.http.post<PaginateList<File>>(`${baseUrl}/fascicolo/${id}/allegati`, filters);
    }

    getDeclaration(id: string | number | null, config: string | number): Observable<Declaration> {
        const params = new HttpParams().set("configurazione", config);
        return this.http.get<Declaration>(`${baseUrl}/fascicolo/${id}/dichiarazione`, { params });
    }

    create(barrierId: number | string): Observable<any> {
        const data = { sbarramento: barrierId };
        return this.http.post(`${baseUrl}/fascicolo`, data);
    }

    update(data: Folder): Observable<any> {
        return this.http.put(`${baseUrl}/fascicolo/${data.uid}/aggiornamento`, {});
    }

    updateDeclaration(id: string, data: Declaration): Observable<any> {
        return this.http.post(`${baseUrl}/fascicolo/${id}/dichiarazione`, data);
    }

    delete(id: number): Observable<any> {
        return this.http.delete(`${baseUrl}/fascicolo/${id}`);
    }

    uploadAttachment(id: string | number, data: File): Observable<any> {
        const attach = new FormData();
        attach.append("descrizione", data.descrizione);
        data.dichiarazione && attach.append("uidDichiarazione", data.dichiarazione.toString());
        data.tipologia && attach.append("tipologia", data.tipologia);
        attach.append("file", data.file);
        return this.http.post(`${baseUrl}/fascicolo/${id}/allegato`, attach);
    }

    deleteAttachment(id: number): Observable<any> {
        return this.http.delete(`${baseUrl}/fascicolo/allegato/${id}`);
    }

    createVersion(id: string | number): Observable<any> {
        return this.http.put(`${baseUrl}/fascicolo/${id}/nuovaversione`, {});
    }

    assign(id: number, cf: string): Observable<any> {
        const data = { cfOperatore: cf };
        return this.http.put(`${baseUrl}/fascicolo/${id}/assegna`, data);
    }

    reassign(id: number, cf: string): Observable<any> {
        const data = { cfOperatore: cf };
        return this.http.put(`${baseUrl}/fascicolo/${id}/riassegna`, data);
    }

    uploadFolderPdf(id: string | number, file: Blob): Observable<HttpResponse<Blob>> {
        const attach = new FormData();
        attach.append("file", file);
        return this.http.post(`${baseUrl}/fascicolo/${id}/genera`, attach, {
            responseType: "blob",
            observe: "response",
        });
    }

    completeFolder(id: string | number, file: Blob): Observable<any> {
        const attach = new FormData();
        attach.append("file", file);
        return this.http.put(`${baseUrl}/fascicolo/${id}/completato`, attach);
    }

    downloadSignedFolderPdf(id: string | number): Observable<HttpResponse<Blob>> {
        return this.http.get(`${baseUrl}/fascicolo/${id}/filefirmato`, {
            responseType: "blob",
            observe: "response",
        });
    }

    close(id: number | string, status: States): Observable<any> {
        const data = { stato: status };
        return this.http.put(`${baseUrl}/fascicolo/${id}/chiudi`, data);
    }

    unlock(id: number | string): Observable<any> {
        return this.http.put(`${baseUrl}/fascicolo/${id}/sblocca`, {});
    }
}
